/**
 * Created by nagib on 10/31/2018.
 */
import React, { useState, useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { browserName } from "react-device-detect";
import PropTypes from "prop-types";
// eslint-disable-next-line import/no-unresolved
import buildConfig from "@regional/config/build.config.json";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import PersonAdd from "@mui/icons-material/PersonAdd";
import InfoIcon from "@mui/icons-material/Info";
import Alert from "@mui/material/Alert";

import HeaderLogo from "../HeaderLogo";
import HelpDialog from "../HelpDialog";
import classes from "./header.module.css";
import UserMenu from "./UserMenu";
import RejectionAlert from "../RejectionAlert";
import GettingStartedDialog from "../GettingStartedDialog";
import ErrorBoundary from "../../../../ErrorBoundary";
import { CustomThemeContext } from "../../../../theme/CustomThemeProvider";
import { useLeftNavConfigMutation } from "../../../../api/core.api.slice";
import { setNavConfig } from "../../slices/leftNav.slice";
import { showNotification } from "../../slices/notification.slice";
import {
  notifyError,
  isElevatedEnv,
  isSSOEnabled,
} from "../../../../utils/helpers";
import { primaryLight, white } from "../../../../theme/color.constants";

function HeaderContent(props) {
  const { user = null, isMaintenance } = props;
  const { pathname } = useLocation();
  const { drawerVisible, setDrawer } = useContext(CustomThemeContext);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  let menuIcon;
  let registerBtn;
  let userMenu;
  let isSandBox;

  const logoClassMap = {
    default: {},
    moLogo: {
      padding: "0.125rem !important",
    },
  };
  const logoStyles =
    logoClassMap[buildConfig?.logoClass] || logoClassMap.default;

  const toggleDrawer = () => {
    setDrawer(!drawerVisible);
  };
  const toggleHelpDialog = () => {
    setShowHelpDialog(!showHelpDialog);
  };
  const helpBtn = (
    <Button onClick={toggleHelpDialog} className={classes.helpBtn}>
      <InfoIcon aria-hidden="true" sx={{ fontSize: ".85rem" }} />
      {process.env.REACT_APP_COMPLIA_INSTALL === "nj-crc" ? "Help/FAQ" : "Help"}
    </Button>
  );
  if (user && !pathname.includes("browser-support")) {
    menuIcon = (
      <IconButton
        className={classes.menuButton}
        aria-label="Menu"
        onClick={toggleDrawer}
        sx={{ display: { xs: "block", md: "none" } }}
      >
        <MenuIcon sx={{ fontSize: { xs: "1.3rem", sm: "1.5rem" } }} />
      </IconButton>
    );
    if (user.addNewUser) menuIcon = null;
    userMenu = <UserMenu toggleHelpDialog={toggleHelpDialog} />;
  }
  if (!user && !isMaintenance && !isSSOEnabled) {
    registerBtn = (
      <Button component={Link} to="/register" className={classes.registerLink}>
        <PersonAdd aria-hidden="true" sx={{ color: primaryLight, mr: "5px" }} />
        <Typography sx={{ color: white }}>Register</Typography>
      </Button>
    );
  }
  if (isElevatedEnv) {
    isSandBox = (
      <Grid
        className={classes.testIndicator}
        size={{ lg: 1, md: 1, sm: 1, xs: 1 }}
      >
        <span className={classes.testIndicatorTitle}>TEST</span>
        <span className={classes.testIndicatorBg} />
      </Grid>
    );
  }

  return (
    <>
      <Grid
        container
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          height: "100%",
        }}
      >
        <Grid className={classes.headerLogo} size={{ md: 3, sm: 5, xs: 8 }}>
          <Grid>{menuIcon}</Grid>
          <Grid className={classes.logoImage} sx={logoStyles}>
            <HeaderLogo />
          </Grid>
        </Grid>
        <Grid size="grow" />
        {!pathname.includes("browser-support") && (
          <>
            <Grid
              size={{ sm: 2.2, md: 2, lg: 1.2 }}
              sx={{
                justifyContent: "end",
                display: { xs: "none", sm: "flex" },
              }}
            >
              {helpBtn}
            </Grid>
            <Grid
              className={isSandBox ? classes.testTagToBackground : null}
              size={{ lg: 1.5, md: 2.1, sm: 3, xs: isSandBox ? 3 : 4 }}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {userMenu}
              {registerBtn}
            </Grid>
            {isSandBox && isSandBox}
          </>
        )}
      </Grid>
      {showHelpDialog && (
        <HelpDialog show={showHelpDialog} setShow={toggleHelpDialog} />
      )}
    </>
  );
}

function Header(props) {
  const { user = null, isMaintenance } = props;
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [showWarning, setShowWarning] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [navConfigData, setNavConfigData] = useState();
  const [getNavConfig] = useLeftNavConfigMutation();
  const isBrowserSupportPage = pathname.includes("browser-support");
  const isNewUser = useSelector((state) => state.auth.userData?.addNewUser);
  const canShowAlerts = Boolean(user && showAlerts);

  useEffect(() => {
    if (user && !isNewUser && !showAlerts) setShowAlerts(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, pathname]);

  useEffect(() => {
    if (
      browserName !== "Chrome" &&
      browserName !== "Safari" &&
      browserName !== "Mobile Safari" &&
      browserName !== "Edge" &&
      browserName !== "Firefox"
    ) {
      setShowWarning(true);
    }
  }, []);

  useEffect(() => {
    if (!navConfigData && user && !isNewUser) {
      const fetchNavConfig = async () => {
        try {
          const { data } = await getNavConfig();
          if (data) {
            dispatch(setNavConfig(data));
            setNavConfigData(data);
          }
        } catch (err) {
          notifyError(dispatch, showNotification, err);
        }
      };
      fetchNavConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isNewUser]);

  const appBarSx = ({ breakpoints }) => ({
    minHeight: "4rem",
    "& .errorFallbackContainer": {
      ml: user && !isBrowserSupportPage ? "18.75rem" : 0,
      [breakpoints.down("md")]: {
        ml: 0,
      },
    },
  });

  return (
    <div className={classes.root}>
      <AppBar position="relative" color="secondary" sx={appBarSx}>
        <ErrorBoundary key="header">
          <Toolbar sx={{ height: "4rem" }} disableGutters>
            <HeaderContent user={user} isMaintenance={isMaintenance} />
          </Toolbar>
        </ErrorBoundary>
      </AppBar>
      {showWarning && !isBrowserSupportPage && !isMaintenance && (
        <Grid
          container
          sx={[
            (theme) => ({
              justifyContent: "flex-end",
              [theme.breakpoints.down("md")]: {
                position: "static",
              },
            }),
            pathname.includes("login")
              ? { position: "absolute" }
              : { position: undefined },
          ]}
        >
          <Grid
            size={{ xs: 12, md: pathname.includes("login") ? 8 : 12 }}
            sx={{ zIndex: "1000" }}
          >
            <Alert
              severity="warning"
              onClose={() => setShowWarning(false)}
              sx={[
                (theme) => ({
                  borderRadius: 0,
                  [theme.breakpoints.down("md")]: { ml: 0 },
                }),
                user ? { ml: "17.84375rem" } : { ml: 0 },
              ]}
            >
              Please use a supported browser for best performance. Please click{" "}
              <Typography
                component={Link}
                variant="body2"
                className="customLink"
                target="_blank"
                rel="noopener noreferrer"
                to="/browser-support"
              >
                here for a list of supported browsers
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      )}
      {canShowAlerts && navConfigData?.showGettingStarted && (
        <GettingStartedDialog />
      )}
      {canShowAlerts && navConfigData?.showRejectionAlert && <RejectionAlert />}
    </div>
  );
}

// Header.whyDidYouRender = true;
Header.propTypes = {
  user: PropTypes.oneOfType([PropTypes.object]),
  isMaintenance: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
    .isRequired,
};
Header.defaultProps = {
  user: null,
};
export default Header;
